<template>
  <div
    ref="root"
    class="msf"
  >
    <!-- Trigger chip -->
    <button
      type="button"
      class="msf-trigger"
      :class="{ 'msf-trigger--active': applied.length > 0, 'msf-trigger--open': isOpen }"
      @click="toggle"
    >
      <feather-icon
        icon="FilterIcon"
        size="11"
        class="msf-trigger__icon"
      />
      <span class="msf-trigger__label">{{ label }}</span>
      <span
        v-if="applied.length"
        class="msf-trigger__count"
      >{{ applied.length }}</span>
      <feather-icon
        :icon="isOpen ? 'ChevronUpIcon' : 'ChevronDownIcon'"
        size="11"
        class="msf-trigger__chevron"
      />
    </button>

    <!-- Dropdown panel -->
    <div
      v-if="isOpen"
      class="msf-panel"
    >
      <input
        v-model="search"
        type="text"
        class="msf-search"
        placeholder="Search the list"
        @click.stop
      >

      <div class="msf-list">
        <!-- Select all row -->
        <label class="msf-item msf-item--all">
          <input
            type="checkbox"
            :checked="allChecked"
            :indeterminate.prop="isIndeterminate"
            @change="toggleAll"
          >
          <span>Select all</span>
        </label>

        <!-- Individual options -->
        <label
          v-for="opt in filteredOptions"
          :key="opt.value"
          class="msf-item"
        >
          <input
            v-model="pending"
            type="checkbox"
            :value="opt.value"
          >
          <span>{{ opt.text }}</span>
        </label>

        <p
          v-if="filteredOptions.length === 0"
          class="msf-empty"
        >
          No results
        </p>
      </div>

      <div class="msf-footer">
        <button
          type="button"
          class="msf-apply"
          @click="apply"
        >
          Add filter
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MultiSelectFilter',
  props: {
    label: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isOpen: false,
      search: '',
      pending: [],
    }
  },
  computed: {
    filteredOptions() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.options
      return this.options.filter(o => o.text.toLowerCase().includes(q))
    },
    allChecked() {
      return this.options.length > 0 && this.pending.length === this.options.length
    },
    isIndeterminate() {
      return this.pending.length > 0 && this.pending.length < this.options.length
    },
    applied() {
      return this.value || []
    },
  },
  watch: {
    value(newVal) {
      this.pending = [...(newVal || [])]
    },
  },
  mounted() {
    document.addEventListener('mousedown', this.onOutsideClick)
  },
  beforeDestroy() {
    document.removeEventListener('mousedown', this.onOutsideClick)
  },
  methods: {
    toggle() {
      if (this.isOpen) {
        this.isOpen = false
      } else {
        this.pending = [...this.applied]
        this.search = ''
        this.isOpen = true
      }
    },
    onOutsideClick(e) {
      if (!this.$refs.root.contains(e.target)) this.isOpen = false
    },
    toggleAll() {
      this.pending = this.allChecked ? [] : this.options.map(o => o.value)
    },
    apply() {
      this.$emit('input', [...this.pending])
      this.isOpen = false
    },
  },
}
</script>

<style lang="scss" scoped>
.msf {
  position: relative;
  display: inline-flex;
}

/* Trigger button */
.msf-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 32px;
  padding: 0 10px;
  border: 1.5px solid #dbeafe;
  border-radius: 20px;
  background: #f8fafc;
  color: #1e3a8a;
  font-size: 0.775rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  &:hover,
  &--open {
    border-color: #1e40af;
    background: #eff6ff;
    color: #1e40af;
  }

  &--active {
    background: #1e40af;
    border-color: #1e40af;
    color: #fff;

    &:hover {
      background: #1d4ed8;
      border-color: #1d4ed8;
    }
  }

  &__icon {
    opacity: 0.7;
  }

  &__count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 4px;
    border-radius: 9px;
    background: rgba(255, 255, 255, 0.3);
    font-size: 0.65rem;
    font-weight: 700;
    line-height: 1;
  }

  &--active &__count {
    background: rgba(255, 255, 255, 0.25);
    color: #fff;
  }

  &:not(&--active) &__count {
    background: #1e40af;
    color: #fff;
  }

  &__chevron {
    opacity: 0.7;
  }
}

/* Dropdown panel */
.msf-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 1050;
  min-width: 210px;
  max-width: 280px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(30, 58, 138, 0.12);
  overflow: hidden;
}

/* Search input */
.msf-search {
  display: block;
  width: 100%;
  padding: 8px 12px;
  border: none;
  border-bottom: 1px solid #f0f4f8;
  font-size: 0.8rem;
  color: #1e3a8a;
  background: #f8fafc;
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }

  &:focus {
    background: #fff;
    border-bottom-color: #1e40af;
  }
}

/* Options list */
.msf-list {
  max-height: 220px;
  overflow-y: auto;
  padding: 6px 0;
}

.msf-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 14px;
  font-size: 0.8rem;
  color: #1e3a8a;
  cursor: pointer;
  margin: 0;
  transition: background 0.1s;

  &:hover {
    background: #eff6ff;
  }

  &--all {
    color: #64748b;
    border-bottom: 1px solid #f0f4f8;
    margin-bottom: 4px;
    padding-bottom: 8px;
  }

  input[type='checkbox'] {
    flex-shrink: 0;
    width: 15px;
    height: 15px;
    accent-color: #1e40af;
    cursor: pointer;
  }

  span {
    user-select: none;
    line-height: 1.3;
  }
}

.msf-empty {
  padding: 10px 14px;
  font-size: 0.78rem;
  color: #94a3b8;
  margin: 0;
}

/* Footer with "Add filter" */
.msf-footer {
  padding: 8px 12px;
  border-top: 1px solid #f0f4f8;
  text-align: right;
  background: #f8fafc;
}

.msf-apply {
  padding: 5px 14px;
  border: none;
  border-radius: 6px;
  background: #1e40af;
  color: #fff;
  font-size: 0.775rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover {
    background: #1d4ed8;
  }
}
</style>
